<template>
    <div id="content">
        <router-link :to="$route.path.split('/').slice(0, -1).join('/')" class="back-link"><i class="bi bi-chevron-left"></i> {{ $t('users.user.back') }}</router-link>
        <div class="admin-card edit-card">
            <div v-if="loading" class="text-center py-4"><span class="loading"></span></div>
            <div v-if="!loading">
                <div class="admin-section-label">
                    <i class="bi bi-person-gear"></i>
                    {{ $t('users.user.title') }}
                </div>
                <div class="form-grid">
                    <div class="form-row">
                        <label class="form-label">{{ $t('users.user.form.username') }}</label>
                        <div class="form-value">{{ user.username }}</div>
                    </div>
                    <div class="form-row">
                        <label class="form-label">{{ $t('users.user.form.fio') }}</label>
                        <div class="form-value">{{ fullName || '—' }}</div>
                    </div>
                    <div class="form-row">
                        <label class="form-label">{{ $t('users.user.form.company') }}</label>
                        <div class="form-value">{{ user.company_name || '—' }}</div>
                    </div>
                    <div class="form-row">
                        <label class="form-label">{{ $t('users.user.form.position') }}</label>
                        <div class="form-value">{{ user.position || '—' }}</div>
                    </div>
                    <div class="form-row">
                        <label class="form-label">{{ $t('users.user.form.phone') }}</label>
                        <div class="form-value">{{ user.phone || '—' }}</div>
                    </div>
                    <div class="form-row">
                        <label class="form-label">{{ $t('users.user.form.registered') }}</label>
                        <div class="form-value">{{ formatDate(user.registered_at) }}</div>
                    </div>
                    <div class="form-row">
                        <label class="form-label">{{ $t('users.user.form.activePlan') }}</label>
                        <select v-model="user.pl_id" class="form-select">
                            <option v-for="plan in plans" :value="plan.id">{{ plan.title }} | {{ plan.price.toLocaleString() }} ₸ / {{ cycles[plan.cycle - 1].text || $t('users.user.form.monthAbbr') }}</option>
                        </select>
                    </div>
                    <div class="form-row">
                        <label class="form-label">{{ $t('users.user.form.role') }}</label>
                        <select v-model="user.role" class="form-select">
                            <option v-for="option in options" :value="option.value">{{ option.text }}</option>
                        </select>
                    </div>
                    <div class="form-row">
                        <label class="form-label">{{ $t('users.user.form.status') }}</label>
                        <div class="d-flex align-items-center gap-2 flex-wrap">
                            <span class="admin-pill" :class="user.email_verified ? 'admin-pill-success' : 'admin-pill-warn'">
                                {{ user.email_verified ? $t('users.user.status.verified') : $t('users.user.status.notVerified') }}
                            </span>
                            <button type="button" class="btn-ghost btn-ghost-sm" @click="toggleConfirm">
                                {{ user.email_verified ? $t('users.user.actions.cancelConfirm') : $t('users.user.actions.confirmEmail') }}
                            </button>
                        </div>
                    </div>
                    <div class="form-row">
                        <label class="form-label">{{ $t('users.user.form.password') }}</label>
                        <button type="button" class="btn-ghost btn-ghost-sm" @click="showPasswordChangeModal=true">
                            <i class="bi bi-key"></i>
                            <span>{{ $t('users.user.actions.changePassword') }}</span>
                        </button>
                    </div>
                </div>
                <div class="form-actions">
                    <button type="button" class="btn-purple" @click="submit">
                        <i class="bi bi-check2"></i>
                        <span>{{ $t('users.user.save') }}</span>
                    </button>
                    <button type="button" class="btn-ghost" @click="$router.go(-1)">{{ $t('users.user.cancel') }}</button>
                    <button v-if="showDelete" type="button" class="btn-danger-soft ms-auto" @click="showDeleteModal=true">
                        <i class="bi bi-trash"></i>
                        <span>{{ $t('users.user.delete') }}</span>
                    </button>
                </div>
            </div>
        </div>
        <div class="admin-card edit-card mt-4" style="max-width: 900px;">
        <div class="admin-section-label">
            <i class="bi bi-link-45deg"></i>
            {{ $t('users.user.qr.title') }}
        </div>
        <div v-if="loadingSurveys" class="text-center py-3"><span class="loading"></span></div>
        <div v-else-if="!surveysWithQr.length" class="text-muted">{{ $t('users.user.qr.empty') }}</div>
        <div v-else class="table-responsive admin-mini-table">
            <table class="table mb-0">
                <thead>
                    <tr>
                        <th>{{ $t('users.user.qr.columns.id') }}</th>
                        <th>{{ $t('users.user.qr.columns.name') }}</th>
                        <th>{{ $t('users.user.qr.columns.links') }}</th>
                        <th>{{ $t('users.user.qr.columns.created') }}</th>
                        <th>{{ $t('users.user.qr.columns.answers') }}</th>
                        <th>{{ $t('users.user.qr.columns.clicks') }}</th>
                        <th>{{ $t('users.user.qr.columns.status') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="s in surveysWithQr" :key="s.id">
                        <td>{{ s.id }}</td>
                        <td>{{ s.surveyname || '-' }}</td>
                        <td>
                            <div v-if="s.links.length">
                                <a v-for="link in s.links" :key="link.id" :href="'https://2mv.io/' + link.code" target="_blank" class="d-block link-2mv">
                                    2mv.io/{{ link.code }}
                                </a>
                            </div>
                            <span v-else class="text-muted">-</span>
                        </td>
                        <td>{{ formatDate(s.createdat) }}</td>
                        <td>{{ s.answercount }}</td>
                        <td>{{ s.click || 0 }}</td>
                        <td>
                            <span class="admin-pill" :class="s.removed ? 'admin-pill-danger' : 'admin-pill-success'">
                                {{ s.removed ? $t('users.user.qr.status.removed') : $t('users.user.qr.status.active') }}
                            </span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        </div>
    </div>
    <Modal
        v-model="showDeleteModal"
        :title="user.username"
        :content="$t('users.user.deleteConfirmHtml')"
        @confirm="confirmDelete"
    />
    <ModalPasswordChange
        v-model="showPasswordChangeModal"
        ref="passwordChange"
        @confirm="changePassword"
    />
</template>

<script>
import Modal from '../Modal2.vue'
import ModalPasswordChange from '../ModalPasswordChange.vue'
import { cycles } from '../../../lib/utils'

export default {
    data:() => ({
        user: {},
        plans: [],
        currentUser: {},
        cycles: cycles,
        loading: false,
        showDelete: false,
        showDeleteModal: false,
        showPasswordChangeModal: false,
        surveys: [],
        subjectRows: [],
        loadingSurveys: false
    }),
    components: { Modal, ModalPasswordChange },
    computed: {
        options() {
            return [
                { text: this.$t('users.user.roles.admin'), value: 'admin' },
                { text: this.$t('users.user.roles.user'), value: 'user' }
            ]
        },
        surveysWithQr() {
            return this.surveys.map(s => {
                let rows = this.subjectRows.filter(r => r.superqrid === s.id)
                let links = rows.map(r => ({
                    id: r.id,
                    code: Number(r.id).toString(36)
                }))
                return { ...s, links }
            })
        },
        fullName() {
            let parts = [this.user?.last_name, this.user?.first_name].filter(Boolean)
            return parts.join(' ')
        }
    },
    async mounted() {
        this.$emit('setPageTitle', this.$t('users.user.title'))
        this.loading = true
        this.currentUser = await this.getCurrentUser()
        this.user = await this.getUserById()
        if(this.user?.result === 'ERROR') this.$router.push('/users')
        this.plans = await this.getPlans()
        this.showDelete = this.currentUser.id != this.user.id
        this.loading = false
        this.loadSurveys()
    },
    methods: {
        async getPlans() {
            let response = await this.$api.getPlans()
            if(response.result === 'SUCCESS') return response.data.plans
        },
        async getCurrentUser() {
            let r = await this.$api.getUser()
            if(r.result === 'SUCCESS') return r.data
            return false
        },
        async getUserById() {
            let r = await this.$api.getUserById(this.$route.params.id)
            if(r.result === 'SUCCESS') return r.data
            this.$notify({ text: r.msg, type: 'error' })
            return false
        },
        async submit() {
            this.loading = true
            let response = await this.$api.updateUser(this.user)
            this.$notify({ text: response?.msg, type: response.result?.toLowerCase() || 'error' })
            this.loading = false
        },
        async confirmDelete() {
            let response = await this.$api.deleteUser(this.user.id)
            if(response.result === 'SUCCESS') {
                this.$router.push('/users')
            } else {
                this.$notify({ text: response.msg, type: 'error' })
            }
        },
        async loadSurveys() {
            this.loadingSurveys = true
            let r = await this.$api.getUserSurveys(this.$route.params.id)
            if(r.result === 'SUCCESS') {
                this.surveys = r.data.surveys || []
                this.subjectRows = r.data.subjectRows || []
            }
            this.loadingSurveys = false
        },
        formatDate(ts) {
            if(!ts) return '-'
            let d = new Date(Number(ts) * 1000)
            return d.toLocaleDateString('ru-RU')
        },
        async toggleConfirm() {
            let r = await this.$api.confirmUser(this.user.id)
            this.$notify({ text: r.msg, type: r.result?.toLowerCase() || 'error' })
            if(r.result === 'SUCCESS') this.user.email_verified = this.user.email_verified ? 0 : 1
        },
        async changePassword(data) {
            if(!data.password || !data.passwordConfirm || data.password !== data.passwordConfirm) return
            let response = await this.$api.changePassword(this.user.id, data)
            this.$notify({ text: response.msg, type: response.result?.toLowerCase() || 'error' })
            if(response.result === 'SUCCESS') this.$refs.passwordChange.closeModal()
        }
    }
}
</script>

<style scoped>
.back-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #64748b;
    font-size: 13px;
    font-weight: 500;
    text-decoration: none;
    margin-bottom: 14px;
    transition: color 0.15s ease;
}
.back-link:hover {
    color: #6d28d9;
}
.back-link i {
    font-size: 14px;
}

.edit-card {
    padding: 24px;
    max-width: 600px;
}

.form-grid {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.form-row {
    display: grid;
    grid-template-columns: 160px 1fr;
    align-items: center;
    gap: 16px;
}

.form-row .form-label {
    margin-bottom: 0;
    font-size: 13px;
    color: #64748b;
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
}

.form-value {
    font-weight: 500;
    color: #0f172a;
    font-size: 14px;
}

.form-actions {
    display: flex;
    gap: 10px;
    margin-top: 24px;
    padding-top: 18px;
    border-top: 1px solid #e5e7eb;
    flex-wrap: wrap;
    align-items: center;
}

.btn-ghost-sm {
    padding: 6px 12px;
    font-size: 13px;
}

.btn-danger-soft {
    background: #fee2e2;
    border: 1px solid #fecaca;
    color: #991b1b;
    padding: 10px 18px;
    border-radius: 10px;
    font-weight: 500;
    font-size: 14px;
    cursor: pointer;
    transition: background 0.15s ease;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}
.btn-danger-soft:hover {
    background: #fecaca;
}

.link-2mv {
    color: #6d28d9;
    text-decoration: none;
    font-size: 13px;
}
.link-2mv:hover {
    text-decoration: underline;
}

.admin-mini-table {
    overflow-x: auto;
}
.admin-mini-table .table {
    border-collapse: separate;
    border-spacing: 0;
    margin: 0;
    font-size: 13px;
}
.admin-mini-table thead th {
    background: #faf7ff;
    color: #475569;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 700;
    padding: 10px 12px;
    border-bottom: 1px solid #e5e7eb;
}
.admin-mini-table tbody td {
    padding: 10px 12px;
    color: #1e293b;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
}
.admin-mini-table tbody tr:hover {
    background-color: #faf7ff;
}
</style>
